import { Controller, FieldValues, Path, RegisterOptions, useFormContext } from 'react-hook-form';
import { useRulesToDefaultMessage } from '@libs/forms';
import { ComponentProps, useEffect, ReactNode, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@atoms/buttons';
import { FaIcon } from '@atoms/icons';
import { RoundPaper } from '@atoms/layout';
import { Box, Stack, Tooltip, alpha, styled, useTheme, Typography } from '@mui/material';

type FormImageUploadFieldProps<T extends FieldValues> = ComponentProps<typeof AttachmentUploadField> & {
  name: Path<T>;
  requiredMessage?: boolean;
  options?: RegisterOptions<T, Path<T>>;
  required?: boolean;
  maxFileSize?: number;
  accept?: string[];
  disabled?: boolean;
};
/**
 * Wrapper around `ImageUploadField` that integrates with react-hook-form.
 */
export function FormAttachmentUploadField<T extends FieldValues>({
  name,
  options,
  requiredMessage,
  required,
  maxFileSize,
  onChange,
  ...props
}: FormImageUploadFieldProps<T>) {
  const intl = useIntl();
  const { control } = useFormContext<T>();
  const parsedOptions = useRulesToDefaultMessage(options);
  const rules = {
    ...parsedOptions,
    ...(required
      ? {
          required: requiredMessage ?? intl.formatMessage({ id: 'forms.fieldIsRequired' }),
        }
      : {}),
    validate: (v: File | null) => {
      if (v && maxFileSize && v.size > maxFileSize) {
        return intl.formatMessage({ id: 'common.fileValidationError' }, { sizeMb: maxFileSize / 1024 / 1024 });
      } else {
        return true;
      }
    },
  };

  return (
    <Controller<T>
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <AttachmentUploadField
          error={error?.message}
          {...field}
          onChange={(file) => {
            // To be able to set form value and trigger onChange outside of the component
            field.onChange?.(file);
            onChange?.(file);
          }}
          {...props}
        />
      )}
    />
  );
}

type ImageUploadFieldProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  buttonTitle: ReactNode;
  deleteButtonTitle?: ReactNode;
  onChange?: (file: File | null) => void;
  initialImgSrc: string;
  accept?: string;
  error?: string;
  disabled?: boolean;
};
function AttachmentUploadField({ onChange, initialImgSrc, accept = '.png,.jpg,.jpeg,.pdf', error, disabled }: ImageUploadFieldProps) {
  const theme = useTheme();
  const [src, setSrc] = useState<string>('');

  useEffect(() => {
    if (initialImgSrc) {
      setSrc(initialImgSrc);
    }
  }, [initialImgSrc]);

  const inputRef = useRef<HTMLInputElement>(null);
  const updateFile = (file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = function (e) {
        if (e.target?.result) {
          setSrc(e.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    } else {
      setSrc('');
    }
    onChange?.(file);
  };

  if (src) {
    const isPDF = src.toLowerCase().endsWith('.pdf');
    const isPDFLoading = src.toLowerCase().includes('data:application/pdf;base64,');
    return (
      <Stack direction={'column'} alignItems={'start'}>
        <Stack direction={'row'}>
          {(isPDF || isPDFLoading) && (
            <Tooltip
              title={
                <FormattedMessage
                  id={
                    isPDFLoading
                      ? 'molecules.attachmentUpload.gotAttachment.tooltip'
                      : 'molecules.attachmentUpload.view.tooltip'
                  }
                />
              }
            >
              <Button
                startIcon={<FaIcon name="file-lines" color={theme.palette.primary.main} />}
                onClick={() => {
                  !isPDFLoading && window.open(src, '_blank')?.focus();
                }}
              >
                <FormattedMessage
                  id={
                    isPDFLoading
                      ? 'molecules.attachmentUpload.gotAttachment.label'
                      : 'molecules.attachmentUpload.view.label'
                  }
                />
              </Button>
            </Tooltip>
          )}
          {!disabled &&<Button
            startIcon={<FaIcon name="trash-can" />}
            variant="passive"
            onClick={() => {
              updateFile(null);
              if (inputRef.current) {
                inputRef.current.value = '';
              }
            }}
          >
            <FormattedMessage id={'molecules.attachmentUpload.delete'} />
          </Button>}
        </Stack>
        {!isPDF && !isPDFLoading && <img src={src} style={{ maxWidth: '100%' }} />}
        <Typography variant='caption' color={"error"}>{error}</Typography>
      </Stack>
    );
  }

  if(!src && disabled) {
    return (<>
      <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary })}>
        <FormattedMessage id={'molecules.attachmentUpload.disabled'} />
      </Typography>
    </>)
  }

  return (
    <RoundPaper>
      <Stack direction={'column'} alignItems={'center'}>
        <Stack
          alignItems={'center'}
          sx={(theme) => ({
            width: '40px',
            height: '40px',
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            borderRadius: '40px',
          })}
        >
          <Box flex={1} />
          <FaIcon name="file-arrow-up" color={theme.palette.primary.main} width={20} height={20} />
          <Box flex={1} />
        </Stack>
        <Button component="label" role={undefined} variant="text" tabIndex={-1}>
          <Typography
            variant="subtitle1"
            sx={(theme) => ({ color: theme.palette.primary.main, textDecoration: 'underline' })}
          >
            <FormattedMessage id={'molecules.attachmentUpload.upload'} />
          </Typography>
          {/* <Typography variant="subtitle1" sx={(theme) => ({ marginLeft: '2px', color: theme.palette.text.primary })}>
            <FormattedMessage id={'molecules.attachmentUpload.subUpload'} />
          </Typography> */}
          <VisuallyHiddenInput
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              updateFile(file);
            }}
          />
        </Button>
        <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary })}>
          <FormattedMessage id={'molecules.attachmentUpload.helper'} />
        </Typography>
      </Stack>
    </RoundPaper>
  );
}
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});
